import React, { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import AxiosInstance from 'src/components/Axios';
import { invalidateSupportChannels } from 'src/hooks/useSupportChannels';
import {
	CForm, CFormLabel, CFormInput, CButton, CRow, CCol,
	CCard, CCardHeader, CCardBody, CFormCheck,
} from '@coreui/react';
import { CenterLoader } from 'src/components/myComponents/CenterLoader';
import FormSubmitOverlay from 'src/components/myComponents/FormSubmitOverlay/FormSubmitOverlay';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const urlApi = '/support-channels';
const urlRouter = '/support-channels';

const SupportChannelsForm = ({ user_data }) => {
	const { id } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const isCreateMode = !id;

	const [isEdit, setIsEdit] = useState(isCreateMode || location.pathname.includes('/update/'));
	useEffect(() => {
		setIsEdit(isCreateMode || location.pathname.includes('/update/'));
	}, [location.pathname, isCreateMode]);

	const [data, setData] = useState(null);
	const [code, setCode] = useState('');
	const [name, setName] = useState('');
	const [sortOrder, setSortOrder] = useState(0);
	const [isActive, setIsActive] = useState(true);
	const [loading, setLoading] = useState(!!id);
	const [submitting, setSubmitting] = useState(false);
	const [errors, setErrors] = useState({});

	useEffect(() => {
		if (!id) return;
		setLoading(true);
		AxiosInstance.get(`${urlApi}/${id}`)
			.then(res => {
				const item = res.data;
				setData(item);
				setCode(item.code || '');
				setName(item.name || '');
				setSortOrder(item.sort_order ?? 0);
				setIsActive(item.is_active ?? true);
			})
			.catch(() => toast.error('Помилка завантаження'))
			.finally(() => setLoading(false));
	}, [id]);

	const hasChanges = !isCreateMode && data !== null && (
		code !== (data.code || '') ||
		name !== (data.name || '') ||
		Number(sortOrder) !== (data.sort_order ?? 0) ||
		isActive !== (data.is_active ?? true)
	);

	const handleReset = useCallback(() => {
		if (!data) return;
		setCode(data.code || '');
		setName(data.name || '');
		setSortOrder(data.sort_order ?? 0);
		setIsActive(data.is_active ?? true);
		setErrors({});
	}, [data]);

	const handleBack = useCallback(() => navigate(-1), [navigate]);

	const handleSubmit = async () => {
		const newErrors = {
			code: !code.trim(),
			name: !name.trim(),
		};
		if (newErrors.code || newErrors.name) {
			setErrors(newErrors);
			const missing = [
				newErrors.code && 'Код',
				newErrors.name && 'Назва',
			].filter(Boolean);
			toast.warning(`Не заповнено: ${missing.join(', ')}`);
			return;
		}
		setErrors({});

		const payload = {
			code: code.trim(),
			name: name.trim(),
			sort_order: Number(sortOrder),
			is_active: isActive,
		};

		if (isCreateMode) {
			try {
				const res = await AxiosInstance.post(urlApi, payload);
				invalidateSupportChannels();
				toast.success('Створено!');
				navigate(`${urlRouter}/view/${res.data.id}`, { replace: true });
			} catch (err) {
				toast.error(err.response?.data?.error || 'Помилка збереження');
			}
			return;
		}

		setSubmitting(true);
		try {
			const res = await AxiosInstance.put(`${urlApi}/${id}`, payload);
			const item = res.data;
			setData(item);
			setCode(item.code || '');
			setName(item.name || '');
			setSortOrder(item.sort_order ?? 0);
			setIsActive(item.is_active ?? true);
			invalidateSupportChannels();
			toast.success('Оновлено!');
		} catch (err) {
			toast.error(err.response?.data?.error || 'Помилка збереження');
		} finally {
			setSubmitting(false);
		}
	};

	const viewFields = data ? [
		{ key: 'id', label: 'ID', value: String(data.id) },
		{ key: 'code', label: 'Код', value: data.code || '' },
		{ key: 'name', label: 'Назва', value: data.name || '' },
		{ key: 'sort_order', label: 'Порядок сортування', value: String(data.sort_order ?? 0) },
		{ key: 'is_active', label: 'Активна', value: data.is_active ? 'Так' : 'Ні' },
	] : [];

	const cardTitle = isCreateMode
		? 'Новий канал зв\'язку'
		: (isEdit ? 'Канал зв\'язку — редагування' : 'Канал зв\'язку — перегляд');

	return (
		<>
			<ToastContainer />
			<CCard className="mb-4">
				<CCardHeader>
					<strong>{cardTitle}</strong>
				</CCardHeader>
				<CCardBody>
					{loading ? (
						<CenterLoader />
					) : (
						<FormSubmitOverlay submitting={submitting}>
							<CForm className="mb-3">
								{isEdit ? (
									<CRow className="g-3">
										<CCol md={3}>
											<CFormLabel>Код *</CFormLabel>
											<CFormInput
												value={code}
												onChange={(e) => {
													setCode(e.target.value);
													if (errors.code) setErrors(prev => ({ ...prev, code: false }));
												}}
												placeholder="Наприклад: email"
												autoComplete="off"
												invalid={!!errors.code}
											/>
										</CCol>
										<CCol md={5}>
											<CFormLabel>Назва *</CFormLabel>
											<CFormInput
												value={name}
												onChange={(e) => {
													setName(e.target.value);
													if (errors.name) setErrors(prev => ({ ...prev, name: false }));
												}}
												placeholder="Наприклад: Email"
												autoComplete="off"
												invalid={!!errors.name}
											/>
										</CCol>
										<CCol md={2}>
											<CFormLabel>Порядок</CFormLabel>
											<CFormInput
												type="number"
												value={sortOrder}
												onChange={(e) => setSortOrder(e.target.value)}
												min={0}
												autoComplete="off"
											/>
										</CCol>
										<CCol md={2} className="d-flex align-items-end pb-1">
											<span title="Визначає, чи відображається цей канал зв'язку у фільтрах і формах.">
												<CFormCheck
													id="isActiveCheck"
													label="Активна"
													checked={isActive}
													onChange={(e) => setIsActive(e.target.checked)}
												/>
											</span>
										</CCol>
									</CRow>
								) : (
									<CRow className="g-3">
										{viewFields.map(({ key, label, value }) => (
											<CCol md={6} key={key}>
												<CFormLabel>{label}</CFormLabel>
												<CFormInput type="text" value={value} disabled readOnly />
											</CCol>
										))}
									</CRow>
								)}

								<div className="d-flex justify-content-start mt-4">
									<CButton color="secondary" variant="outline" onClick={handleBack} title="Повернутись до попередньої сторінки">
										← Назад
									</CButton>
									{!isEdit && (
										<CButton color="success" className="ms-2" onClick={() => navigate(`${urlRouter}/update/${id}`)} title="Перейти до форми редагування">
											Редагувати
										</CButton>
									)}
									{isEdit && (
										<CButton
											color="success"
											className="ms-2"
											onClick={handleSubmit}
											disabled={!isCreateMode && !hasChanges}
											title={isCreateMode ? 'Створити новий канал зв\'язку' : 'Зберегти зміни'}
										>
											{isCreateMode ? 'Створити' : 'Зберегти'}
										</CButton>
									)}
									{isEdit && !isCreateMode && (
										<CButton
											color="secondary"
											variant="outline"
											className="ms-2"
											onClick={handleReset}
											disabled={!hasChanges}
											title="Скинути форму до останніх збережених значень"
										>
											Скинути
										</CButton>
									)}
									{isEdit && !isCreateMode && (
										<CButton
											color="danger"
											variant="outline"
											className="ms-2"
											onClick={() => navigate(`${urlRouter}/view/${id}`)}
											title="Скасувати редагування і повернутись до перегляду"
										>
											До перегляду
										</CButton>
									)}
								</div>
							</CForm>
						</FormSubmitOverlay>
					)}
				</CCardBody>
			</CCard>
		</>
	);
};

export default SupportChannelsForm;
